import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ChevronDownIcon } from 'lucide-react';
const faqs = [{
  question: 'What exactly is KittyPot?',
  answer: 'KittyPot is a shared wallet for groups. Everyone adds money upfront into a "kitty," expenses are logged as you go, and KittyPot automatically tracks who spent what. When the kitty closes, everyone gets back exactly what they\'re owed. No chasing. No spreadsheets.'
}, {
  question: 'How is this different from Splitwise?',
  answer: "Splitwise tracks who owes who — but you still have to chase people for the money. KittyPot is different: everyone adds money upfront into a shared pot. There's nothing to chase because the money is already there. It's a shared wallet, not a bill-splitting app."
}, {
  question: 'Does KittyPot charge any fees?',
  answer: "KittyPot is completely free to use. There's a small fee on deposits (2.5% + 25p) to cover secure payment processing and keep KittyPot running. No subscriptions, no hidden charges — that's it."
}, {
  question: 'How do expenses work?',
  answer: "You pay for things as you normally would — with your own card, Apple Pay, or however you like. Then you log the expense in the app. KittyPot tracks everything in real time and automatically calculates who's owed what when the kitty closes."
}, {
  question: 'What happens when a kitty closes?',
  answer: "When you close a kitty, KittyPot automatically calculates the final balances. Everyone who is owed money gets it back. No awkward messages, no manual transfers — it's all handled for you."
}, {
  question: 'Is my money safe?',
  answer: 'Yes. All payments are securely processed by Stripe — a regulated provider trusted by Amazon, Shopify, and Google. Your funds are ring-fenced within your group and protected by 256-bit encryption. You can withdraw your money anytime.'
}, {
  question: 'Do I need to trust everyone in the group?',
  answer: "Because everyone adds money upfront, there's no risk of someone not paying you back. The money is already in the kitty before anyone spends. You can also see every transaction in real time, so there's full transparency."
}, {
  question: 'How do people join my kitty?',
  answer: 'You create a kitty in the app and share an invite link with your group. Anyone with the link can join and add money. It takes less than 30 seconds to set up.'
}, {
  question: 'How can I add money to a kitty?',
  answer: "You can add money via Apple Pay, Google Pay, or bank card. It's instant and secure."
}, {
  question: 'Can I withdraw my money?',
  answer: 'Yes. You can withdraw your share from a kitty at any time. When you leave a kitty, your balance is returned to you automatically.'
}, {
  question: 'When is KittyPot launching?',
  answer: "We're currently in early access and launching soon in the UK. Join the waitlist to get priority access and be one of the first to try it."
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <div className="min-h-screen bg-[#FAFAFA] font-sans selection:bg-kitty-yellow-200 selection:text-kitty-dark" data-id="element-515">
      <Navbar data-id="element-516" />
      <main className="pt-36 pb-24" data-id="element-517">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-518">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-center mb-14" data-id="element-519">
            <h1 className="text-4xl md:text-5xl font-black text-kitty-dark mb-4 tracking-tight" data-id="element-520">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-500 max-w-lg mx-auto" data-id="element-521">
              Everything you need to know about KittyPot. Can't find what you're
              looking for?{' '}
              <a href="/contact" className="text-kitty-yellow-600 hover:text-kitty-yellow-700 font-medium underline underline-offset-2" data-id="element-522">
                Get in touch
              </a>
              .
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="space-y-3" data-id="element-523">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-524">
                <button onClick={() => toggle(index)} className="w-full flex items-center justify-between px-6 py-5 text-left gap-4" data-id="element-525">
                  <span className="text-base font-semibold text-kitty-dark leading-snug" data-id="element-526">
                    {faq.question}
                  </span>
                  <motion.div animate={{
                rotate: openIndex === index ? 180 : 0
              }} transition={{
                duration: 0.2
              }} className="flex-shrink-0" data-id="element-527">
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-528" />
                  </motion.div>
                </button>
                <AnimatePresence initial={false} data-id="element-529">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-530">
                      <div className="px-6 pb-5 pt-0" data-id="element-531">
                        <p className="text-gray-500 leading-relaxed text-[15px]" data-id="element-532">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </motion.div>

          {/* Bottom CTA */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="text-center mt-16 bg-gray-950 rounded-2xl p-10" data-id="element-533">
            <h2 className="text-2xl font-bold text-white mb-3" data-id="element-534">
              Still have questions?
            </h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto" data-id="element-535">
              We're happy to help. Reach out and we'll get back to you within 24
              hours.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-536">
              <a href="/contact" className="bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-3 rounded-full font-bold transition-all active:scale-95 shadow-md hover:shadow-lg" data-id="element-537">
                Contact Us
              </a>
              <a href="mailto:support@kittypot.com" className="text-gray-400 hover:text-white font-medium transition-colors text-sm" data-id="element-538">
                or email support@kittypot.com
              </a>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-539" />
    </div>;
}